import { useState, useEffect, useRef } from "react";
import { Sun, Moon, User, Briefcase, MessageSquareQuote, Layers, Code2, Newspaper, Mail } from "lucide-react";
import { cn } from "../lib/utils";

const NAV_ITEMS = [
  { href: "#about",      id: "about",      label: "Über mich",  Icon: User },
  { href: "#leistungen", id: "leistungen", label: "Schwerpunkte", Icon: Layers },
  { href: "#technologien", id: "technologien", label: "Werkzeugkasten", Icon: Code2 },
  { href: "#erfahrung",  id: "erfahrung",  label: "Erfahrung",  Icon: Briefcase },
  { href: "#referenzen", id: "referenzen", label: "Referenzen", Icon: MessageSquareQuote },
  { href: "#blog",       id: "blog",       label: "Blog",       Icon: Newspaper },
  { href: "#kontakt",    id: "kontakt",    label: "Kontakt",    Icon: Mail },
];

const SECTION_IDS = NAV_ITEMS.map((n) => n.id);

function useTheme() {
  // Starts light to match the prerendered HTML. The inline script in index.html has
  // already applied a saved dark theme to <html>, so we only pick that up here.
  const [dark, setDark] = useState(false);
  const synced = useRef(false);

  useEffect(() => {
    if (!synced.current) {
      synced.current = true;
      const saved = document.documentElement.classList.contains("dark");
      if (saved !== dark) { setDark(saved); return; }
    }
    const root = document.documentElement;
    try {
      if (dark) { root.classList.add("dark"); localStorage.setItem("theme", "dark"); }
      else       { root.classList.remove("dark"); localStorage.setItem("theme", "light"); }
    } catch {}
  }, [dark]);

  return [dark, () => setDark((d) => !d)];
}

function useActiveSection() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const observers = [];
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) setActive(id); },
        { rootMargin: "-40% 0px -55% 0px" }
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return active;
}

const NavDock = () => {
  const [dark, toggleDark] = useTheme();
  const active = useActiveSection();

  const linkCls = (id) =>
    cn(
      "flex h-8 items-center overflow-hidden rounded-md px-2 transition-colors",
      "max-lg:h-7",
      "hover:text-accent hover:bg-accent/10",
      active === id
        ? "text-accent bg-accent/10"
        : "text-muted-foreground"
    );

  return (
    <nav
      data-testid="nav-dock"
      className="group fixed bottom-4 left-1/2 z-50 -translate-x-1/2
                 lg:top-1/2 lg:bottom-auto lg:left-6 lg:translate-x-0 lg:-translate-y-1/2"
    >
      <div
        className="bg-card flex w-fit flex-row items-stretch gap-1.5 rounded-lg border
                   p-2 shadow-sm max-lg:p-1.5 lg:flex-col lg:rounded-2xl"
      >
        {NAV_ITEMS.map(({ href, id, label, Icon }) => (
          <a
            key={id}
            href={href}
            data-testid={`nav-${id}`}
            aria-label={label}
            aria-current={active === id ? "location" : undefined}
            className={linkCls(id)}
          >
            <Icon className="size-4 shrink-0" strokeWidth={1.8} />
            {/* Label expands on desktop hover */}
            <span className="hidden text-sm whitespace-nowrap transition-all duration-300 ease-out
                             lg:inline-block lg:max-w-0 lg:opacity-0
                             lg:group-hover:ml-2 lg:group-hover:max-w-32 lg:group-hover:opacity-100">
              {label}
            </span>
          </a>
        ))}

        {/* Separator */}
        <div className="bg-border h-px w-full hidden lg:block" />
        <div className="bg-border w-px h-full lg:hidden" />

        {/* Theme toggle */}
        <button
          data-testid="theme-toggle"
          onClick={toggleDark}
          aria-label="Farbschema wechseln"
          className={cn(
            linkCls(""),
            "flex h-8 items-center rounded-md px-2 max-lg:h-7"
          )}
        >
          {dark
            ? <Sun  className="size-4 shrink-0" strokeWidth={1.8} />
            : <Moon className="size-4 shrink-0" strokeWidth={1.8} />
          }
          <span className="hidden text-sm whitespace-nowrap transition-all duration-300 ease-out
                           lg:inline-block lg:max-w-0 lg:opacity-0
                           lg:group-hover:ml-2 lg:group-hover:max-w-32 lg:group-hover:opacity-100">
            {dark ? "Hell" : "Dunkel"}
          </span>
        </button>
      </div>
    </nav>
  );
};

export default NavDock;
